// src/components/items/IncomingModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "components/ui/Modal";
import { Button } from "components/ui/Button";
import { TextField } from "components/ui/TextField";
import { createIncomingAct } from "api/items";
import { apiGet } from "lib/apiClient";

export const IncomingModal = ({ open, onClose, onCreated, fixedItemId }) => {
  const [counterparty, setCounterparty] = useState("Поставщик");
  const [rows, setRows] = useState([{ item_id: "", qty: "", price: "" }]);
  const [items, setItems] = useState([]);
  const [fixedItem, setFixedItem] = useState(null);

  const [loading, setLoading] = useState(false);
  const [addingRow, setAddingRow] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState({});

  const isSingle = !!fixedItemId;

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setError(null);
        if (fixedItemId) {
          const { data, error } = await apiGet(`/items/${fixedItemId}/`);
          if (error) throw new Error(error);
          setFixedItem(data || null);
          setRows([{ item_id: String(fixedItemId), qty: "", price: "" }]);
        } else {
          const { data, error } = await apiGet('/items/');
          if (error) throw new Error(error);
          setItems(data || []);
          setRows([{ item_id: "", qty: "", price: "" }]);
        }
        setCounterparty("Поставщик");
        setTouched({});
      } catch (e) {
        setError(e.message || "Не удалось загрузить товары");
      }
    })();
  }, [open, fixedItemId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (ev) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        handleClose();
      }
      if (ev.key === "Enter" && (ev.metaKey || ev.ctrlKey)) {
        ev.preventDefault();
        submit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rows, counterparty, loading]);

  const addRow = () => {
    setAddingRow(true);
    setRows((prev) => [...prev, { item_id: "", qty: "", price: "" }]);
    setTimeout(() => setAddingRow(false), 100);
  };
  const rmRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));
  const update = (i, patch) =>
    setRows((prev) =>
      prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r))
    );

  const parsedPositions = useMemo(() => {
    return rows
      .map((r) => ({
        item_id: r.item_id ? Number(r.item_id) : null,
        qty: r.qty === "" ? "" : Number(r.qty),
        price: r.price === "" ? "" : Number(r.price),
      }))
      .filter((r) => r.item_id !== null);
  }, [rows]);

  const canSubmit = useMemo(() => {
    if (!counterparty.trim()) return false;
    if (parsedPositions.length === 0) return false;
    for (const p of parsedPositions) {
      if (p.qty === "" || Number.isNaN(p.qty) || p.qty <= 0) return false;
      if (p.price !== "" && (Number.isNaN(p.price) || p.price < 0))
        return false;
    }
    return true;
  }, [counterparty, parsedPositions]);

  const fieldError = (rowIdx, key) => {
    const wasTouched = !!touched[`${rowIdx}:${key}`];
    if (!wasTouched) return null;
    const row = parsedPositions[rowIdx];
    if (!row) return null;
    if (
      key === "qty" &&
      (row.qty === "" || Number.isNaN(row.qty) || row.qty <= 0)
    )
      return "Кол-во > 0";
    if (
      key === "price" &&
      row.price !== "" &&
      (Number.isNaN(row.price) || row.price < 0)
    )
      return "Цена ≥ 0";
    return null;
  };

  const resetLocal = () => {
    setRows([
      { item_id: fixedItemId ? String(fixedItemId) : "", qty: "", price: "" },
    ]);
    setCounterparty("Поставщик");
    setError(null);
    setTouched({});
  };

  const handleClose = () => {
    if (loading) return;
    onClose?.();
    resetLocal();
  };

  const submit = async () => {
    const newTouched = {};
    rows.forEach((_, i) => {
      newTouched[`${i}:qty`] = true;
      newTouched[`${i}:price`] = true;
    });
    setTouched(newTouched);

    if (!canSubmit) {
      setError("Проверьте корректность данных");
      return;
    }

    const positions = parsedPositions.map((r) => {
      // Если цена не указана, отправляем 0, чтобы сервер мог взять цену из товара
      const price = r.price !== "" && r.price !== null && !Number.isNaN(r.price) ? r.price : null;
      return {
        item_id: r.item_id,
        qty: r.qty,
        price: price,
      };
    });

    setLoading(true);
    setError(null);
    try {
      await createIncomingAct({ counterparty: counterparty.trim(), positions });
      onCreated?.();
      handleClose();
    } catch (e) {
      setError(e.message || "Ошибка оформления прихода");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isSingle ? "Приход по товару" : "Оформление прихода"}
      className="bg-gray-50 text-gray-800 dark:bg-slate-900 dark:text-slate-100"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="text-xs text-gray-500 dark:text-slate-400">
            {canSubmit
              ? "Ctrl/⌘+Enter для оформления"
              : "Заполните обязательные поля"}
          </div>
          <div className="flex gap-4">
            <Button variant="ghost" onClick={handleClose} disabled={loading}>
              Отмена
            </Button>
            <Button
              onClick={submit}
              disabled={loading || !canSubmit}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? "Оформление..." : "Оформить приход"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        <TextField
          label="Поставщик"
          value={counterparty}
          onChange={(e) => setCounterparty(e.target.value)}
          placeholder="Например, ООО «ТехСнаб»"
          required
          hint="Название организации или ФИО"
          inputClassName="bg-white border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
        />

        {isSingle ? (
          <div className="space-y-5">
            <div className="rounded-lg border border-gray-200 bg-white dark:bg-slate-800 dark:border-slate-700 p-4 text-sm">
              <div className="text-gray-600 dark:text-slate-400">
                Товар:{" "}
                <span className="font-semibold text-gray-900 dark:text-slate-200">
                  {fixedItem?.name || fixedItemId}
                  {fixedItem?.supplier_name && (
                    <span className="ml-2 text-xs font-normal text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">
                      {fixedItem.supplier_name}
                    </span>
                  )}
                </span>
              </div>
              {typeof fixedItem?.qty_on_hand !== "undefined" && (
                <div className="text-xs text-gray-500 dark:text-slate-400 mt-1.5">
                  Текущий остаток:{" "}
                  <b className="text-gray-700 dark:text-slate-300">{fixedItem.qty_on_hand}</b>{" "}
                  {fixedItem?.unit || ""}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField
                type="number"
                inputMode="decimal"
                min="0.001"
                step="0.001"
                label="Количество"
                value={rows[0]?.qty ?? ""}
                onChange={(e) => update(0, { qty: e.target.value })}
                onBlur={() => setTouched((t) => ({ ...t, ["0:qty"]: true }))}
                invalid={!!fieldError(0, "qty")}
                hint={fieldError(0, "qty") || "Допускаются дробные значения"}
                inputClassName="bg-white border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                hintClassName={fieldError(0, "qty") ? "text-red-600" : ""}
              />
              <TextField
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                label="Цена закупки (опционально)"
                value={rows[0]?.price ?? ""}
                onChange={(e) => update(0, { price: e.target.value })}
                onBlur={() => setTouched((t) => ({ ...t, ["0:price"]: true }))}
                invalid={!!fieldError(0, "price")}
                hint={
                  fieldError(0, "price") ||
                  "Если не указать, цена не сохранится"
                }
                inputClassName="bg-white border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                hintClassName={fieldError(0, "price") ? "text-red-600" : ""}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:bg-slate-800 dark:border-slate-700">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-slate-700/50">
                  <tr className="text-left text-xs uppercase text-gray-500 dark:text-slate-400">
                    <th className="px-4 py-3 font-medium">Товар</th>
                    <th className="px-4 py-3 font-medium w-32 text-right">
                      Кол-во
                    </th>
                    <th className="px-4 py-3 font-medium w-36 text-right">
                      Цена (опц.)
                    </th>
                    <th className="px-4 py-3 w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {rows.map((r, i) => (
                    <tr key={i} className="align-top">
                      <td className="px-4 py-3">
                        <select
                          className="w-full rounded-md border-gray-300 dark:border-slate-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100"
                          value={r.item_id}
                          onChange={(e) =>
                            update(i, { item_id: e.target.value })
                          }
                        >
                          <option value="">— Выберите товар —</option>
                          {items.map((it) => (
                            <option key={it.id} value={it.id}>
                              {it.name}
                              {it.supplier_name ? ` (${it.supplier_name})` : ""}
                              {typeof it.qty_on_hand !== "undefined"
                                ? ` | Остаток: ${it.qty_on_hand}`
                                : ""}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0.001"
                          step="0.001"
                          value={r.qty}
                          onChange={(e) => update(i, { qty: e.target.value })}
                          onBlur={() =>
                            setTouched((t) => ({ ...t, [`${i}:qty`]: true }))
                          }
                          className={`w-full rounded-md border-gray-300 dark:border-slate-700 px-3 py-2 text-right shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 ${fieldError(i, "qty") ? "border-red-500" : ""
                            }`}
                        />
                        {fieldError(i, "qty") && (
                          <div className="mt-1 text-xs text-red-600 text-center">
                            {fieldError(i, "qty")}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          value={r.price}
                          onChange={(e) => update(i, { price: e.target.value })}
                          onBlur={() =>
                            setTouched((t) => ({ ...t, [`${i}:price`]: true }))
                          }
                          className={`w-full rounded-md border-gray-300 dark:border-slate-700 px-3 py-2 text-right shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 ${fieldError(i, "price") ? "border-red-500" : ""
                            }`}
                        />
                        {fieldError(i, "price") && (
                          <div className="mt-1 text-xs text-red-600 text-center">
                            {fieldError(i, "price")}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => rmRow(i)}
                          className="text-red-600 hover:bg-red-50 px-2 py-2"
                          aria-label={`Удалить строку ${i + 1}`}
                          title="Удалить"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={addRow}
                disabled={addingRow}
                title="Добавить позицию"
                className="border-gray-300 hover:bg-gray-100 dark:border-slate-700 dark:hover:bg-slate-800 dark:text-slate-200"
              >
                + Добавить позицию
              </Button>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-md border border-red-300 bg-red-100 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}
      </div>
    </Modal >
  );
};
